import { useEffect, useRef } from 'react';

import type { FetchItemSummariesByCatalogIdQuery } from '@/redux/modules/item/summary/itemSummary.api';

import { useAppDispatch } from '@/redux/hooks';
import { fetchCatalogItemSummaries } from '@/redux/modules/catalog/items/catalogItems.actions';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import TEST_HOUSES from '@/utils/testHouses';

type Params = {
    catalogId: number;
    currentItemId: number;
    liveLotIndex: number | undefined;
    sellerId: number;
    showLive: boolean;
};

/**
 * Load the live lot + next few for the catalog console.
 * Keyed on clerk lot id, not listing/PubNub index — those values flicker and used to retrigger forever.
 * Always pass a numeric `index`; undefined is treated as a full listing fetch and replaces the lot grid.
 */
export const useFetchLiveCatalogConsoleItems = ({
    catalogId,
    currentItemId,
    liveLotIndex,
    sellerId,
    showLive,
}: Params) => {
    const dispatch = useAppDispatch();
    const lastFetchedKeyRef = useRef('');

    useEffect(() => {
        if (!showLive || currentItemId <= 0) {
            lastFetchedKeyRef.current = '';
            return;
        }

        const fetchKey = `${catalogId}:${currentItemId}:${sellerId}`;
        if (lastFetchedKeyRef.current === fetchKey) {
            return;
        }
        lastFetchedKeyRef.current = fetchKey;

        const query: FetchItemSummariesByCatalogIdQuery = { index: liveLotIndex ?? 0, pageSize: 4 };
        if (TEST_HOUSES.includes(sellerId)) {
            query.preview = true;
        }

        dispatch(
            fetchItemSummaryIfNeeded({
                identifier: 'catalog-console',
                itemId: currentItemId,
            })
        );
        dispatch(fetchCatalogItemSummaries({ catalogId, query }));
    }, [catalogId, currentItemId, dispatch, liveLotIndex, sellerId, showLive]);
};
